import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { isAuthenticated, isTokenExpired } from "@/lib/auth";
import { clearAuthToken } from "@/services/apiClient";
import { startAuthorization } from "@/services/mcpOAuth";
import { SmplifyLogo } from "@/components/SmplifyLogo";

const isPreviewEnv = (() => {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h.includes("lovableproject.com") || h.includes("lovable.app");
})();

export default function LoginPage() {
  const [ssoLoading, setSsoLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated() && !isTokenExpired()) {
      navigate("/dashboard", { replace: true });
    } else if (isAuthenticated() && isTokenExpired()) {
      clearAuthToken();
      try {
        localStorage.removeItem("smplify_tenant_id");
      } catch {
        // ignore
      }
    }
  }, [navigate]);

  const handleSso = async () => {
    setAuthError(null);
    setSsoLoading(true);
    try {
      await startAuthorization("/dashboard");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Sign-in failed";
      setAuthError(msg);
      toast.error(msg);
      setSsoLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <main className="w-full max-w-7xl px-12 py-8 flex flex-col md:flex-row items-center justify-center gap-20">
        <div className="w-full md:w-1/2 flex justify-center">
          <SmplifyLogo
            size="login"
            // @ts-ignore
            fetchpriority="high"
            width="300"
            height="225"
          />
        </div>
        <div className="w-full md:w-1/2 flex justify-center">
          <div className="w-full max-w-md space-y-6 bg-card rounded-lg shadow-lg border p-8">
            {isPreviewEnv && (
              <Alert>
                <AlertDescription className="text-xs">
                  Sign-in may be unreliable in the Lovable preview. For production, use{" "}
                  <a
                    href="https://edr.smplify.com/login"
                    className="text-primary hover:underline font-medium"
                  >
                    edr.smplify.com
                  </a>
                  .
                </AlertDescription>
              </Alert>
            )}
            {authError && (
              <Alert variant="destructive">
                <AlertDescription>{authError}</AlertDescription>
              </Alert>
            )}

            <div className="text-center">
              <h1 className="text-3xl font-bold text-foreground">Welcome back</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Sign in to Smplify EDR
              </p>
            </div>

            <Button
              type="button"
              size="lg"
              className="w-full"
              disabled={ssoLoading}
              onClick={handleSso}
            >
              {ssoLoading ? (
                "Redirecting…"
              ) : (
                <>
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  Sign in with Smplify
                </>
              )}
            </Button>

            <p className="text-center text-xs text-muted-foreground">
              Issued by{" "}
              <span className="font-medium text-foreground">mcp.smplify.com</span>{" "}
              — one session for the portal, EDR, and Lighthouse.
            </p>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-3 text-muted-foreground tracking-wide">
                  Use legacy email &amp; password
                </span>
              </div>
            </div>

            <div className="space-y-3 text-center text-sm">
              <a
                href="https://console.smplify.com/forgot-password"
                className="block font-medium text-foreground hover:underline"
              >
                Forgot your password?
              </a>
              <p className="text-muted-foreground">
                New user?{" "}
                <a
                  href="https://console.smplify.com/register"
                  className="font-medium text-foreground hover:underline"
                >
                  Create an account
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
